import { useModel as _useModel, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, withModifiers as _withModifiers, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "d-flex"
}
const _hoisted_2 = { class: "address-location" }
const _hoisted_3 = {
  key: 0,
  class: "text-medium-emphasis text-body-2"
}
const _hoisted_4 = { class: "address-location" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 2 }
const _hoisted_8 = { key: 3 }
const _hoisted_9 = { class: "text-medium-emphasis text-body-2" }

import { ref, computed, inject, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { email, maxLength, required } from '@vuelidate/validators';
import { useI18n } from '@/shared/composables/i18n';
import type { UserProfileAddressDto, UserProfileResponse, UserProfileUpdateRequest } from '@/venit/services/model/techpubs-cms';
import { RetailUserAddressType } from '@/venit/services/model/techpubs-cms';
import UserProfileService from '@/venit/user/profile/user-profile.service';
import ConfirmDialog from '@/venit/components/confirm-dialog/confirm-dialog.vue';
import { useAccountStore } from '@/stores/account';


export default /*@__PURE__*/_defineComponent({
  __name: 'user-profile-dialog',
  props: {
    "modelValue": { type: Boolean, ...{ default: false } },
    "modelModifiers": {},
  },
  emits: ["update:modelValue"],
  setup(__props) {

const open = _useModel<boolean>(__props, "modelValue");

const { t } = useI18n();
const userProfileService = inject<() => UserProfileService>('userProfileService')!;
const accountStore = useAccountStore();
const confirmDialog = ref<InstanceType<typeof ConfirmDialog> | null>(null);
const addressFormRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

const profile = ref<UserProfileResponse | null>(null);
const activeTab = ref('basicData');
const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);

const editedProfile = ref<UserProfileUpdateRequest>({ firstName: '', lastName: '', email: '', langKey: 'en' });

const validationRules = {
  email: { required, email },
  firstName: { maxLength: maxLength(50) },
  lastName: { maxLength: maxLength(50) },
};
const v$ = useVuelidate(validationRules, editedProfile);

const emailErrors = computed(() => {
  if (!v$.value.email.$dirty) return [];
  const errors: string[] = [];
  if (v$.value.email.required.$invalid) errors.push(t('admin-users.validation.email'));
  if (v$.value.email.email.$invalid) errors.push(t('admin-users.validation.email'));
  return errors;
});

const firstNameErrors = computed(() => {
  if (!v$.value.firstName.$dirty) return [];
  return v$.value.firstName.maxLength.$invalid ? [t('admin-users.validation.firstName')] : [];
});

const lastNameErrors = computed(() => {
  if (!v$.value.lastName.$dirty) return [];
  return v$.value.lastName.maxLength.$invalid ? [t('admin-users.validation.lastName')] : [];
});

const langOptions = ['pl', 'en'];

const loadProfile = () => {
  userProfileService()
    .getProfile()
    .then(response => {
      profile.value = response.data;
      editedProfile.value = {
        firstName: response.data.firstName ?? '',
        lastName: response.data.lastName ?? '',
        email: response.data.email,
        langKey: response.data.langKey ?? 'en',
      };
    });
};

const saveProfile = async () => {
  await v$.value.$touch();
  if (v$.value.$invalid) return;
  errorMessage.value = null;
  successMessage.value = null;
  userProfileService()
    .updateProfile(editedProfile.value)
    .then(response => {
      profile.value = response.data;
      successMessage.value = t('user-profile.saveSuccess');
    })
    .catch(() => {
      errorMessage.value = t('user-profile.saveError');
    });
};

// Addresses
const addresses = ref<UserProfileAddressDto[]>([]);
const addressDialog = ref(false);
const editedAddress = ref<UserProfileAddressDto | null>(null);

const addressTypeOrder = [RetailUserAddressType.SHIPPING, RetailUserAddressType.BILLING];

const addressTypeOptions = computed(() => addressTypeOrder.map(type => ({
  value: type,
  title: t(`global.form.address.type.${type.toLowerCase()}`),
})));

const addressTableHeaders = computed(() => [
  { title: t('global.form.address.type'), key: 'type', sortable: false },
  { title: t('admin-organization.table.header.name'), key: 'name', sortable: false },
  { title: t('global.form.address.table.location'), key: 'location', sortable: false },
  { title: t('admin-organization.table.header.phone'), key: 'phone', sortable: false },
  { title: t('global.form.address.email'), key: 'email', sortable: false },
  { title: t('global.form.address.table.actions'), key: 'actions', sortable: false, align: 'end' as const },
]);

const loadAddresses = () => {
  userProfileService()
    .listAddresses()
    .then(response => {
      addresses.value = response.data.addresses;
    });
};

const addAddress = () => {
  editedAddress.value = {
    id: null, version: null, type: null,
    name: null, taxId: null, country: null, state: null,
    zip: null, city: null, addressLine1: null, addressLine2: null,
    phone: null, email: editedProfile.value.email ?? null,
  };
  addressDialog.value = true;
};

const openAddressEditDialog = (address: UserProfileAddressDto) => {
  editedAddress.value = { ...address };
  addressDialog.value = true;
};

const cloneAddress = (address: UserProfileAddressDto) => {
  editedAddress.value = { ...address, id: null, version: null };
  addressDialog.value = true;
};

const closeAddressDialog = () => {
  addressDialog.value = false;
  editedAddress.value = null;
};

const saveAddress = async () => {
  const { valid } = await addressFormRef.value!.validate();
  if (!valid) return;
  userProfileService()
    .saveAddress({ address: editedAddress.value })
    .then(response => {
      const saved = response.data.address;
      const idx = addresses.value.findIndex(a => a.id === saved.id);
      if (idx >= 0) {
        addresses.value[idx] = saved;
      } else {
        addresses.value.push(saved);
      }
      closeAddressDialog();
      accountStore.loadUser();
    });
};

const deleteAddress = async (address: UserProfileAddressDto) => {
  if (await confirmDialog.value?.openWithMessage(t('confirm-dialog.deleteMessage').toString())) {
    userProfileService()
      .deleteAddress({ addressId: address.id })
      .then(() => {
        loadAddresses();
        accountStore.loadUser();
      });
  }
};

watch(open, (isOpen) => {
  if (isOpen) {
    activeTab.value = 'basicData';
    errorMessage.value = null;
    successMessage.value = null;
    loadProfile();
    loadAddresses();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_toolbar_title = _resolveComponent("v-toolbar-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_tabs_window_item = _resolveComponent("v-tabs-window-item")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_tabs_window = _resolveComponent("v-tabs-window")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: open.value,
    "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((open).value = $event)),
    persistent: "",
    scrollable: "",
    "max-width": "1200px"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_toolbar, {
            color: "surface-variant",
            density: "compact"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_toolbar_title, null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('user-profile.page.title')), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }),
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                icon: "",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (open.value = false))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, null, {
                    default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                      _createTextVNode("mdi-close", -1 /* CACHED */)
                    ]))]),
                    _: 1 /* STABLE */
                  })
                ]),
                _: 1 /* STABLE */
              })
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(_component_v_card_text, null, {
            default: _withCtx(() => [
              (errorMessage.value)
                ? (_openBlock(), _createBlock(_component_v_alert, {
                    key: 0,
                    type: "warning",
                    class: "mb-4"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(errorMessage.value), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true),
              (successMessage.value)
                ? (_openBlock(), _createBlock(_component_v_alert, {
                    key: 1,
                    type: "success",
                    class: "mb-4"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(successMessage.value), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true),
              (profile.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createVNode(_component_v_tabs, {
                      modelValue: activeTab.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((activeTab).value = $event)),
                      direction: "vertical"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_tab, { value: "basicData" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { start: "" }, {
                              default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                                _createTextVNode("mdi-account", -1 /* CACHED */)
                              ]))]),
                              _: 1 /* STABLE */
                            }),
                            _createTextVNode(" " + _toDisplayString(_ctx.$t('user-profile.tab.basicData')), 1 /* TEXT */)
                          ]),
                          _: 1 /* STABLE */
                        }),
                        _createVNode(_component_v_tab, { value: "addresses" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { start: "" }, {
                              default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                                _createTextVNode("mdi-map-marker-outline", -1 /* CACHED */)
                              ]))]),
                              _: 1 /* STABLE */
                            }),
                            _createTextVNode(" " + _toDisplayString(_ctx.$t('user-profile.tab.addresses')), 1 /* TEXT */)
                          ]),
                          _: 1 /* STABLE */
                        })
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["modelValue"]),
                    _createVNode(_component_v_tabs_window, {
                      modelValue: activeTab.value,
                      "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((activeTab).value = $event)),
                      class: "flex-grow-1"
                    }, {
                      default: _withCtx(() => [
                        _createCommentVNode(" Basic data tab "),
                        _createVNode(_component_v_tabs_window_item, { value: "basicData" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_form, {
                              ref: "basicDataForm",
                              onSubmit: _withModifiers(saveProfile, ["prevent"])
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_container, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, {
                                          cols: "12",
                                          sm: "8"
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_text_field, {
                                              modelValue: editedProfile.value.email,
                                              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((editedProfile.value.email) = $event)),
                                              label: _ctx.$t('admin-users.user.email'),
                                              "error-messages": emailErrors.value,
                                              onBlur: _cache[3] || (_cache[3] = ($event: any) => (_unref(v$).email.$touch()))
                                            }, null, 8 /* PROPS */, ["modelValue", "label", "error-messages"]),
                                            _createVNode(_component_v_text_field, {
                                              modelValue: editedProfile.value.firstName,
                                              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((editedProfile.value.firstName) = $event)),
                                              label: _ctx.$t('admin-users.user.firstName'),
                                              "error-messages": firstNameErrors.value,
                                              onBlur: _cache[5] || (_cache[5] = ($event: any) => (_unref(v$).firstName.$touch()))
                                            }, null, 8 /* PROPS */, ["modelValue", "label", "error-messages"]),
                                            _createVNode(_component_v_text_field, {
                                              modelValue: editedProfile.value.lastName,
                                              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((editedProfile.value.lastName) = $event)),
                                              label: _ctx.$t('admin-users.user.lastName'),
                                              "error-messages": lastNameErrors.value,
                                              onBlur: _cache[7] || (_cache[7] = ($event: any) => (_unref(v$).lastName.$touch()))
                                            }, null, 8 /* PROPS */, ["modelValue", "label", "error-messages"]),
                                            _createVNode(_component_v_select, {
                                              modelValue: editedProfile.value.langKey,
                                              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((editedProfile.value.langKey) = $event)),
                                              items: langOptions,
                                              label: _ctx.$t('admin-users.user.lang')
                                            }, null, 8 /* PROPS */, ["modelValue", "label"])
                                          ]),
                                          _: 1 /* STABLE */
                                        })
                                      ]),
                                      _: 1 /* STABLE */
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "auto" }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_btn, {
                                              type: "submit",
                                              variant: "flat",
                                              color: "primary",
                                              disabled: _unref(v$).$invalid
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_ctx.$t('user-profile.save')), 1 /* TEXT */)
                                              ]),
                                              _: 1 /* STABLE */
                                            }, 8 /* PROPS */, ["disabled"])
                                          ]),
                                          _: 1 /* STABLE */
                                        })
                                      ]),
                                      _: 1 /* STABLE */
                                    })
                                  ]),
                                  _: 1 /* STABLE */
                                })
                              ]),
                              _: 1 /* STABLE */
                            }, 512 /* NEED_PATCH */)
                          ]),
                          _: 1 /* STABLE */
                        }),
                        _createCommentVNode(" Addresses tab "),
                        _createVNode(_component_v_tabs_window_item, { value: "addresses" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_container, { fluid: "" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_row, {
                                  justify: "end",
                                  class: "mb-2"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_col, { cols: "auto" }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_btn, {
                                          icon: "",
                                          size: "small",
                                          color: "primary",
                                          onClick: addAddress
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_icon, null, {
                                              default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                                                _createTextVNode("mdi-plus", -1 /* CACHED */)
                                              ]))]),
                                              _: 1 /* STABLE */
                                            })
                                          ]),
                                          _: 1 /* STABLE */
                                        })
                                      ]),
                                      _: 1 /* STABLE */
                                    })
                                  ]),
                                  _: 1 /* STABLE */
                                }),
                                _createVNode(_component_v_data_table, {
                                  headers: addressTableHeaders.value,
                                  items: addresses.value,
                                  "items-per-page": -1,
                                  "hide-default-footer": "",
                                  density: "compact"
                                }, {
                                  "item.type": _withCtx(({ item }) => [
                                    _createTextVNode(_toDisplayString(_ctx.$t(`global.form.address.type.${item.type.toLowerCase()}`)), 1 /* TEXT */)
                                  ]),
                                  "item.name": _withCtx(({ item }) => [
                                    _createElementVNode("div", _hoisted_2, [
                                      _createElementVNode("span", null, _toDisplayString(item.name), 1 /* TEXT */),
                                      (item.taxId)
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_3, "(" + _toDisplayString(_ctx.$t('global.form.address.taxId.label')) + ": " + _toDisplayString(item.taxId) + ")", 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true)
                                    ])
                                  ]),
                                  "item.location": _withCtx(({ item }) => [
                                    _createElementVNode("div", _hoisted_4, [
                                      (item.addressLine1)
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(item.addressLine1), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true),
                                      (item.addressLine2)
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(item.addressLine2), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true),
                                      (item.zip || item.city)
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString([item.zip, item.city].filter(Boolean).join(' ')), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true),
                                      (item.country)
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(item.country), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true)
                                    ])
                                  ]),
                                  "item.actions": _withCtx(({ item }) => [
                                    _createVNode(_component_v_btn, {
                                      icon: "",
                                      size: "x-small",
                                      variant: "text",
                                      onClick: ($event: any) => (openAddressEditDialog(item)),
                                      title: _ctx.$t('global.form.address.edit')
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, null, {
                                          default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                                            _createTextVNode("mdi-pencil", -1 /* CACHED */)
                                          ]))]),
                                          _: 1 /* STABLE */
                                        })
                                      ]),
                                      _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["onClick", "title"]),
                                    _createVNode(_component_v_btn, {
                                      icon: "",
                                      size: "x-small",
                                      variant: "text",
                                      onClick: ($event: any) => (cloneAddress(item)),
                                      title: _ctx.$t('global.form.address.clone')
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, null, {
                                          default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                                            _createTextVNode("mdi-content-duplicate", -1 /* CACHED */)
                                          ]))]),
                                          _: 1 /* STABLE */
                                        })
                                      ]),
                                      _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["onClick", "title"]),
                                    _createVNode(_component_v_btn, {
                                      icon: "",
                                      size: "x-small",
                                      variant: "text",
                                      onClick: ($event: any) => (deleteAddress(item))
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, null, {
                                          default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                                            _createTextVNode("mdi-delete", -1 /* CACHED */)
                                          ]))]),
                                          _: 1 /* STABLE */
                                        })
                                      ]),
                                      _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["onClick"])
                                  ]),
                                  "no-data": _withCtx(() => [
                                    _createElementVNode("span", _hoisted_9, _toDisplayString(_ctx.$t('global.form.address.noAddresses')), 1 /* TEXT */)
                                  ]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["headers", "items"]),
                                _createCommentVNode(" Address edit dialog "),
                                _createVNode(_component_v_dialog, {
                                  modelValue: addressDialog.value,
                                  "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((addressDialog).value = $event)),
                                  persistent: "",
                                  scrollable: "",
                                  "max-width": "700px"
                                }, {
                                  default: _withCtx(() => [
                                    (editedAddress.value)
                                      ? (_openBlock(), _createBlock(_component_v_form, {
                                          key: 0,
                                          ref_key: "addressFormRef",
                                          ref: addressFormRef,
                                          onSubmit: _withModifiers(saveAddress, ["prevent"])
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_card, null, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_v_toolbar, {
                                                  color: "surface-variant",
                                                  density: "compact"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_toolbar_title, null, {
                                                      default: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(editedAddress.value.id ? _ctx.$t('global.form.address.edit') : _ctx.$t('global.form.address.add')), 1 /* TEXT */)
                                                      ]),
                                                      _: 1 /* STABLE */
                                                    }),
                                                    _createVNode(_component_v_spacer),
                                                    _createVNode(_component_v_btn, {
                                                      icon: "",
                                                      onClick: closeAddressDialog
                                                    }, {
                                                      default: _withCtx(() => [
                                                        _createVNode(_component_v_icon, null, {
                                                          default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                                                            _createTextVNode("mdi-close", -1 /* CACHED */)
                                                          ]))]),
                                                          _: 1 /* STABLE */
                                                        })
                                                      ]),
                                                      _: 1 /* STABLE */
                                                    })
                                                  ]),
                                                  _: 1 /* STABLE */
                                                }),
                                                _createVNode(_component_v_card_text, { class: "d-flex flex-column ga-2" }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_select, {
                                                      modelValue: editedAddress.value.type,
                                                      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((editedAddress.value.type) = $event)),
                                                      items: addressTypeOptions.value,
                                                      "item-title": "title",
                                                      "item-value": "value",
                                                      label: _ctx.$t('global.form.address.type'),
                                                      density: "compact",
                                                      rules: [v => !!v || _ctx.$t('admin-validations.required')]
                                                    }, null, 8 /* PROPS */, ["modelValue", "items", "label", "rules"]),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: editedAddress.value.name,
                                                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((editedAddress.value.name) = $event)),
                                                      clearable: "",
                                                      label: _ctx.$t('global.form.address.name'),
                                                      density: "compact",
                                                      rules: [v => !!v || _ctx.$t('global.form.address.name.required')]
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "rules"]),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: editedAddress.value.taxId,
                                                      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((editedAddress.value.taxId) = $event)),
                                                      clearable: "",
                                                      label: _ctx.$t('global.form.address.taxId'),
                                                      density: "compact"
                                                    }, null, 8 /* PROPS */, ["modelValue", "label"]),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: editedAddress.value.country,
                                                      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((editedAddress.value.country) = $event)),
                                                      clearable: "",
                                                      label: _ctx.$t('global.form.address.country'),
                                                      density: "compact",
                                                      rules: [v => !!v || _ctx.$t('admin-validations.required')]
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "rules"]),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: editedAddress.value.state,
                                                      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((editedAddress.value.state) = $event)),
                                                      clearable: "",
                                                      label: _ctx.$t('global.form.address.state'),
                                                      density: "compact",
                                                      rules: [v => !!v || _ctx.$t('admin-validations.required')]
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "rules"]),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: editedAddress.value.zip,
                                                      "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((editedAddress.value.zip) = $event)),
                                                      clearable: "",
                                                      label: _ctx.$t('global.form.address.zip'),
                                                      density: "compact",
                                                      rules: [v => !!v || _ctx.$t('admin-validations.required')]
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "rules"]),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: editedAddress.value.city,
                                                      "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((editedAddress.value.city) = $event)),
                                                      clearable: "",
                                                      label: _ctx.$t('global.form.address.city'),
                                                      density: "compact",
                                                      rules: [v => !!v || _ctx.$t('admin-validations.required')]
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "rules"]),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: editedAddress.value.addressLine1,
                                                      "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((editedAddress.value.addressLine1) = $event)),
                                                      clearable: "",
                                                      label: _ctx.$t('global.form.address.line1'),
                                                      density: "compact",
                                                      rules: [v => !!v || _ctx.$t('admin-validations.required')]
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "rules"]),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: editedAddress.value.addressLine2,
                                                      "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((editedAddress.value.addressLine2) = $event)),
                                                      clearable: "",
                                                      label: _ctx.$t('global.form.address.line2'),
                                                      density: "compact"
                                                    }, null, 8 /* PROPS */, ["modelValue", "label"]),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: editedAddress.value.phone,
                                                      "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((editedAddress.value.phone) = $event)),
                                                      clearable: "",
                                                      label: _ctx.$t('global.form.address.phone'),
                                                      density: "compact",
                                                      rules: [v => !!v || _ctx.$t('admin-validations.required')]
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "rules"]),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: editedAddress.value.email,
                                                      "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((editedAddress.value.email) = $event)),
                                                      clearable: "",
                                                      label: _ctx.$t('global.form.address.email'),
                                                      density: "compact",
                                                      rules: [v => !!v || _ctx.$t('admin-validations.required'), v => !v || /.+@.+\..+/.test(v) || _ctx.$t('global.form.address.email.invalid')]
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "rules"])
                                                  ]),
                                                  _: 1 /* STABLE */
                                                }),
                                                _createVNode(_component_v_card_actions, null, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_spacer),
                                                    _createVNode(_component_v_btn, {
                                                      variant: "outlined",
                                                      onClick: closeAddressDialog
                                                    }, {
                                                      default: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(_ctx.$t('action.cancel')), 1 /* TEXT */)
                                                      ]),
                                                      _: 1 /* STABLE */
                                                    }),
                                                    _createVNode(_component_v_btn, {
                                                      type: "submit",
                                                      variant: "flat",
                                                      color: "primary"
                                                    }, {
                                                      default: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(_ctx.$t('user-profile.save')), 1 /* TEXT */)
                                                      ]),
                                                      _: 1 /* STABLE */
                                                    })
                                                  ]),
                                                  _: 1 /* STABLE */
                                                })
                                              ]),
                                              _: 1 /* STABLE */
                                            })
                                          ]),
                                          _: 1 /* STABLE */
                                        }, 512 /* NEED_PATCH */))
                                      : _createCommentVNode("v-if", true)
                                  ]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["modelValue"]),
                                _createVNode(ConfirmDialog, {
                                  ref_key: "confirmDialog",
                                  ref: confirmDialog
                                }, null, 512 /* NEED_PATCH */)
                              ]),
                              _: 1 /* STABLE */
                            })
                          ]),
                          _: 1 /* STABLE */
                        })
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["modelValue"])
                  ]))
                : _createCommentVNode("v-if", true)
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["modelValue"]))
}
}

})